import { PokeNames } from "types/pokemon/poke-names";

/**
 * Pokemon Sprite Scale Overrides
 *
 * This file contains scale multipliers for pokemon sprites that need
 * size adjustments to match their real-world proportions.
 *
 * Default scale is 1.0 - only add entries for pokemon that need adjustment.
 * Values > 1.0 make the sprite larger, < 1.0 make it smaller.
 *
 * IMPORTANT: Prefer to use POKE_NAMES enum for the keys!
 */
export const POKEMON_SPRITE_SCALE_OVERRIDES: Record<string, number> = {
  // Example overrides - adjust values as needed after visual testing
  // "gardevoir": 1.3,
  // "monferno": 0.85,
  [PokeNames.GARDEVOIR]: 1.1,
  [PokeNames.GARDEVOIR_MEGA]: 1.2,
  [PokeNames.SYLVEON]: 0.95,
  [PokeNames.KAKUNA]: 0.9,
  [PokeNames.METAPOD]: 0.9,
  [PokeNames.PICHU]: 0.9,
  [PokeNames.CHIKORITA]: 0.9,
  [PokeNames.SNORUNT]: 0.85,
  [PokeNames.GLISCOR]: 1.1,
  [PokeNames.URSALUNA]: 1.4,
  [PokeNames.ROWLET]: 0.9,
  [PokeNames.DECIDUEYE]: 1.15,
  [PokeNames.DECIDUEYE_HISUI]: 1.15,
  [PokeNames.CUBCHOO]: 0.85,
  [PokeNames.TORTERRA]: 1.2,
  [PokeNames.METAGROSS]: 1.2,
  [PokeNames.MEDICHAM]: 1.1,
  [PokeNames.MEDICHAM_MEGA]: 1.15,
  [PokeNames.FORRETRESS]: 1.2,
  [PokeNames.GARBODOR]: 1.15,
  [PokeNames.GRENINJA]: 1.2,
  [PokeNames.CHANDELURE]: 1.1,
  [PokeNames.SUNKERN]: 0.85,
  [PokeNames.LEDIAN]: 0.9,
  [PokeNames.EKANS]: 0.85,
  [PokeNames.VENONAT]: 0.85,
  [PokeNames.BUNEARY]: 0.9,
  [PokeNames.KINGLER]: 1.25,
  [PokeNames.SHEDINJA]: 0.9,
  [PokeNames.LATIOS]: 1.2,
  [PokeNames.LATIAS]: 1.2,
  [PokeNames.RAPIDASH]: 1.15,
  [PokeNames.ARCANINE]: 1.15,
  [PokeNames.SNORLAX]: 1.3,
  [PokeNames.TANGELA]: 0.9,
  [PokeNames.DUGTRIO]: 0.95,
  [PokeNames.TORRACAT]: 0.9,
  [PokeNames.CINDERACE]: 1.15,
  [PokeNames.KLEAVOR]: 1.15,
  [PokeNames.WAILMER]: 1.1,
  [PokeNames.PILOSWINE]: 1.1,
  [PokeNames.BLASTOISE]: 1.15,
  [PokeNames.CHARMELEON]: 0.9,
  [PokeNames.CROBAT]: 1.15,
  [PokeNames.PARASECT]: 1.15,
  [PokeNames.GIBLE]: 0.9,
  [PokeNames.TANGROWTH]: 1.15,
  [PokeNames.GLIGAR]: 0.85,
  [PokeNames.DRATINI]: 0.9,
  [PokeNames.RATICATE_ALOLA]: 1.2,
  [PokeNames.WEEZING_GALAR]: 1.2,
  [PokeNames.MEWTWO]: 1.2,
  [PokeNames.MAGNEZONE]: 1.2,
  [PokeNames.KABUTOPS]: 1.15,
  [PokeNames.MAGMAR]: 1.1,
  [PokeNames.SCEPTILE]: 1.1,
  [PokeNames.JYNX]: 1.1,
  [PokeNames.BAGON]: 0.9,
  [PokeNames.NINETALES]: 1.15,
  [PokeNames.NINETALES_ALOLA]: 1.2,
  [PokeNames.GIRAFARIG]: 1.15,
  [PokeNames.WOBBUFFET]: 1.1,
  [PokeNames.NIDOQUEEN]: 1.3,
  [PokeNames.NIDOKING]: 1.3,
  [PokeNames.BEARTIC]: 1.1,
  [PokeNames.FERALIGATR]: 1.1,
  [PokeNames.CHARIZARD]: 1.1,
  [PokeNames.CHARIZARD_MEGA_X]: 1.25,
  [PokeNames.RAIKOU]: 1.4,
  [PokeNames.RAICHU_ALOLA]: 1.05,
  [PokeNames.SANDSLASH_ALOLA]: 1.1,
  [PokeNames.ENTEI]: 1.4,
  [PokeNames.SUICUNE]: 1.4,
  [PokeNames.REGISTEEL]: 1.4,
  [PokeNames.SALAMENCE]: 1.1,
  [PokeNames.DUSKNOIR]: 1.1,
  [PokeNames.VILEPLUME]: 1.2,
  [PokeNames.GALLADE]: 1.2,
  [PokeNames.ARMALDO]: 1.1,
  [PokeNames.MAWILE]: 1.1,
  [PokeNames.CERULEDGE]: 1.1,
  [PokeNames.SWALOT]: 1.15,
  [PokeNames.LUCARIO]: 1.25,
  [PokeNames.MILTANK]: 1.15,
  [PokeNames.PSYDUCK]: 0.9,
  [PokeNames.FEEBAS]: 0.9,
  [PokeNames.GROWLITHE]: 0.9,
  [PokeNames.DUSKULL]: 0.9,
  [PokeNames.SHINX]: 0.9,
  [PokeNames.EXEGGCUTE]: 0.9,
  [PokeNames.BULBASAUR]: 0.9,
  [PokeNames.MAGIKARP]: 0.9,
  [PokeNames.TENTACRUEL]: 1.5,
  [PokeNames.SKARMORY]: 1.1,
  [PokeNames.MAGCARGO]: 1.1,
  [PokeNames.KINGDRA]: 1.1,
  [PokeNames.CHESNAUGHT]: 1.1,
  [PokeNames.VICTREEBEL]: 1.15,
  [PokeNames.LOPUNNY]: 1.15,
  [PokeNames.DONPHAN]: 1.15,
  [PokeNames.SCIZOR]: 1.15,
  [PokeNames.SCYTHER]: 1.1,
  [PokeNames.CLOYSTER]: 1.15,
  [PokeNames.PALOSSAND]: 1.15,
  [PokeNames.CAMERUPT]: 1.15,
  [PokeNames.CAMERUPT_MEGA]: 1.15,
  [PokeNames.KANGASKHAN]: 1.1,
  [PokeNames.METANG]: 1.15,
  [PokeNames.DEWGONG]: 1.15,
  [PokeNames.AGGRON]: 1.2,
  [PokeNames.CONKELDURR]: 1.2,
  [PokeNames.SHARPEDO]: 1.2,
  [PokeNames.EXPLOUD]: 1.2,
  [PokeNames.DRAGALGE]: 1.3,
  [PokeNames.LAPRAS]: 1.1,
  [PokeNames.RHYDON]: 1.1,
  [PokeNames.POLIWRATH]: 1.15,
  [PokeNames.REGICE]: 1.4,
  [PokeNames.DARKRAI]: 1.4,
  [PokeNames.ARCEUS]: 1.25,
  [PokeNames.MAGMORTAR]: 1.2,
  [PokeNames.WEEZING]: 1.1,
  [PokeNames.EXEGGUTOR]: 1.2,
  [PokeNames.EXEGGUTOR_ALOLA]: 1.2,
  [PokeNames.LUNALA]: 1.25,
  [PokeNames.DELPHOX]: 1.25,
  [PokeNames.VIGOROTH]: 0.9,
  [PokeNames.HAUNTER]: 0.9,
  [PokeNames.HOPPIP]: 0.9,
  [PokeNames.GASTLY]: 0.85,
  [PokeNames.SANDSLASH]: 1.1,
  [PokeNames.ARIADOS]: 1.1,
  [PokeNames.PIGNITE]: 1.1,
  [PokeNames.RHYPERIOR]: 1.15,
  [PokeNames.ANNIHILAPE]: 1.15,
  [PokeNames.DODRIO]: 1.1,
  [PokeNames.GRANBULL]: 1.1,
  [PokeNames.TYPHLOSION]: 1.15,
  [PokeNames.MAROWAK]: 1.1,
  [PokeNames.MACHAMP]: 1.15,
  [PokeNames.MAROWAK_ALOLA]: 1.2,
  [PokeNames.AERODACTYL]: 1.15,
  [PokeNames.AERODACTYL_MEGA]: 1.15,
  [PokeNames.LUCARIO_MEGA]: 1.15,
  [PokeNames.LOPUNNY_MEGA]: 1.15,
  [PokeNames.KANGASKHAN_MEGA]: 1.15,
  [PokeNames.ABSOL]: 1.1,
  [PokeNames.ABSOL_MEGA]: 1.1,
  [PokeNames.WYRDEER]: 1.2,
  [PokeNames.YVELTAL]: 1.2,
  [PokeNames.ZEKROM]: 1.2,
  [PokeNames.PERSIAN_ALOLA]: 1.1,
  [PokeNames.DRAGAPULT]: 1.1,
  [PokeNames.BRAVIARY]: 1.15,
  [PokeNames.BRAVIARY_HISUI]: 1.15,
  [PokeNames.OBSTAGOON]: 1.1,
  [PokeNames.STARMIE]: 1.1,
  [PokeNames.ALAKAZAM_MEGA]: 1.1,
  [PokeNames.TYPHLOSION_HISUI]: 1.2,
  [PokeNames.MAMOSWINE]: 1.3,
  [PokeNames.BANETTE_MEGA]: 1.2,
  [PokeNames.HONCHKROW]: 1.1,
  [PokeNames.TORKOAL]: 1.1,
  [PokeNames.HELIOLISK]: 1.1,
  [PokeNames.CURSOLA]: 1.1,
  [PokeNames.ZOROARK_HISUI]: 1.1,
  [PokeNames.ZOROARK]: 1.1,
  [PokeNames.CRYOGONAL]: 1.15,
  [PokeNames.DIANCIE_MEGA]: 1.15,
  [PokeNames.TOGEKISS]: 1.15,
  [PokeNames.CRESSELIA]: 1.25,
  [PokeNames.MUK]: 1.15,
  [PokeNames.MUK_ALOLA]: 1.25,
  [PokeNames.SKELEDIRGE]: 1.15,
  [PokeNames.LATIAS_MEGA]: 1.2,
  [PokeNames.LATIOS_MEGA]: 1.2,
  [PokeNames.GARCHOMP]: 1.1,
  [PokeNames.LUDICOLO]: 1.1,
  [PokeNames.BRONZONG]: 1.1,
  [PokeNames.MANECTRIC]: 1.1,
  [PokeNames.MANECTRIC_MEGA]: 1.15,
  [PokeNames.WALREIN]: 1.25,
  [PokeNames.HIPPOWDON]: 1.15,
  [PokeNames.LUXRAY]: 1.15,
  [PokeNames.FLYGON]: 1.2,
  [PokeNames.SNEASLER]: 1.15,
  [PokeNames.EMBOAR]: 1.15,
  [PokeNames.SUDOWOODO]: 1.15,
  [PokeNames.INCINEROAR]: 1.1,
  [PokeNames.SHELGON]: 0.9,
  [PokeNames.SCRAGGY]: 0.9,
  [PokeNames.DUSCLOPS]: 0.9,
  [PokeNames.HOOTHOOT]: 0.9,
  [PokeNames.TEDDIURSA]: 0.9,
  [PokeNames.KRICKETOT]: 0.9,
  [PokeNames.GRIMER]: 0.95,
  [PokeNames.MAGBY]: 0.9,
  [PokeNames.SKITTY]: 0.9,
  [PokeNames.RATTATA]: 0.9,
  [PokeNames.CATERPIE]: 0.9,
  [PokeNames.TORCHIC]: 0.9,
  [PokeNames.NUZLEAF]: 0.9,
  [PokeNames.SPINDA]: 0.9,
  [PokeNames.STOUTLAND]: 0.9,
  [PokeNames.BEAUTIFLY]: 0.9,
  [PokeNames.CHARMANDER]: 0.9,
  [PokeNames.LEDYBA]: 0.85,
  [PokeNames.REGIROCK]: 1.3,
  [PokeNames.RUNERIGUS]: 1.15,
  [PokeNames.GENGAR_MEGA]: 1.2,
  [PokeNames.GALVANTULA]: 1.1,
  [PokeNames.CRAWDAUNT]: 1.15,
  [PokeNames.TAUROS]: 1.15,
  [PokeNames.GOLURK]: 1.15,
  [PokeNames.XERNEAS]: 1.2,
  [PokeNames.GOGOAT]: 1.2,
  [PokeNames.DRAGONITE]: 1.2,
  [PokeNames.VENUSAUR]: 1.2,
  [PokeNames.TREVENANT]: 1.2,
  [PokeNames.WHISCASH]: 1.25,
  [PokeNames.HEATRAN]: 1.3,
  [PokeNames.RAYQUAZA_MEGA]: 1.3,
  [PokeNames.PALKIA]: 1.2,
  [PokeNames.DIALGA]: 1.2,
  [PokeNames.SAMUROTT]: 1.2,
  [PokeNames.ELECTIVIRE]: 1.2,
  [PokeNames.CLAYDOL]: 1.25,
  [PokeNames.ABOMASNOW]: 1.35,
  [PokeNames.TROPIUS]: 1.35,
  [PokeNames.DRACOZOLT]: 1.15,
  [PokeNames.WAILORD]: 1.1,
  [PokeNames.HITMONCHAN]: 1.1,
  [PokeNames.HITMONLEE]: 1.1,
  [PokeNames.HITMONTOP]: 1.1,
  [PokeNames.AMPHAROS]: 1.15,
  [PokeNames.BEWEAR]: 1.1,
  [PokeNames.TURTONATOR]: 1.1,
  [PokeNames.MILOTIC]: 1.1,
  [PokeNames.MANDIBUZZ]: 1.1,
  [PokeNames.NOCTOWL]: 1.1,
  [PokeNames.COFAGRIGUS]: 1.15,
  [PokeNames.CORVIKNIGHT]: 1.15,
  [PokeNames.BLAZIKEN]: 1.15,
  [PokeNames.SHIFTRY]: 1.15,
  [PokeNames.ALAKAZAM]: 1.1,
  [PokeNames.SWELLOW]: 1.25,
  [PokeNames.TOXICROAK]: 1.15,
  [PokeNames.VIKAVOLT]: 1.15,
  [PokeNames.FERROTHORN]: 1.25,
  [PokeNames.EELEKTROSS]: 1.25,
  [PokeNames.ZWEILOUS]: 1.1,
  [PokeNames.HYDREIGON]: 1.25,
  [PokeNames.SILVALLY]: 1.3,
  [PokeNames.TYRANITAR]: 1.35,
  [PokeNames.TYRANITAR_MEGA]: 1.45,
  [PokeNames.LILLIGANT]: 1.2,
  [PokeNames.LILLIGANT_HISUI]: 1.2,
  [PokeNames.RAPIDASH_GALAR]: 1.2,
  [PokeNames.SLOWKING]: 1.2,
  [PokeNames.SLOWKING_GALAR]: 1.2,
  [PokeNames.PANGORO]: 1.1,
  [PokeNames.GOODRA]: 1.15,
  [PokeNames.GOODRA_HISUI]: 1.15,
  [PokeNames.PINSIR]: 1.15,
  [PokeNames.WHIMSICOTT]: 1.1,
  [PokeNames.SWAMPERT]: 1.1,
  [PokeNames.MIENSHAO]: 1.1,
  [PokeNames.SANDACONDA]: 1.15,
  [PokeNames.BLISSEY]: 1.15,
  [PokeNames.DURALUDON]: 1.1,
  [PokeNames.DRAMPA]: 1.15,
  [PokeNames.KINGAMBIT]: 1.15,
  [PokeNames.ZERAORA]: 1.15,
  [PokeNames.DREDNAW]: 1.15,
  [PokeNames.COBALION]: 1.15,
  [PokeNames.MALAMAR]: 1.15,
  [PokeNames.CRADILY]: 1.15,
  [PokeNames.INFERNAPE]: 1.15,
  [PokeNames.SAMUROTT_HISUI]: 1.2,
  [PokeNames.PIDGEOT]: 1.15,
  [PokeNames.ESCAVALIER]: 1.15,
  [PokeNames.FLORGES]: 1.1,
  [PokeNames.DURANT]: 1.1,
  [PokeNames.CLEFABLE]: 1.1,
  [PokeNames.OMASTAR]: 1.1,
  [PokeNames.AURORUS]: 1.1,
  [PokeNames.STEELIX]: 1.1,
  [PokeNames.MAWILE_MEGA]: 1.2,
  [PokeNames.HAXORUS]: 1.2,
  [PokeNames.TERRAKION]: 1.2,
  [PokeNames.ELECTRODE]: 1.2,
  [PokeNames.MOLTRES_GALAR]: 1.2,
  [PokeNames.ARTICUNO_GALAR]: 1.2,
  [PokeNames.ZAPDOS_GALAR]: 1.2,
  [PokeNames.MOLTRES]: 1.2,
  [PokeNames.ARTICUNO]: 1.2,
  [PokeNames.ZAPDOS]: 1.2,
  [PokeNames.ELECTRODE_HISUI]: 1.2,
  [PokeNames.GOLEM_ALOLA]: 1.2,
  [PokeNames.GOLEM]: 1.2,
  [PokeNames.URSARING]: 1.2,
  [PokeNames.GALLADE_MEGA]: 1.2,
  [PokeNames.SABLEYE_MEGA]: 1.15,
  [PokeNames.ALTARIA_MEGA]: 1.15,
  [PokeNames.HOUNDOOM_MEGA]: 1.15,
  [PokeNames.OVERQWIL]: 1.1,
  [PokeNames.NAGANADEL]: 1.1,
  [PokeNames.GLALIE_MEGA]: 1.1,
  [PokeNames.GENGAR]: 1.1,
  [PokeNames.RAMPARDOS]: 1.15,
  [PokeNames.TYRANTRUM]: 1.15,
  [PokeNames.CRABOMINABLE]: 1.15,
  [PokeNames.VESPIQUEN]: 1.25,
  [PokeNames.MISMAGIUS]: 1.25,
  [PokeNames.LUNATONE]: 1.25,
  [PokeNames.SOLROCK]: 1.25,
  [PokeNames.SLAKING]: 1.35,
  [PokeNames.PANCHAM]: 0.9,
  [PokeNames.SENTRET]: 0.9,
  [PokeNames.SLIGGOO]: 0.9,
  [PokeNames.MARILL]: 0.9,
};

/**
 * Get the sprite scale multiplier for a pokemon.
 * Returns the override value if defined, otherwise 1.0.
 */
export function getPokemonSpriteScale(basePokemonName: string): number {
  const scale = POKEMON_SPRITE_SCALE_OVERRIDES[basePokemonName];
  if (scale) return scale;

  const parsedName = basePokemonName
    .replace(/__mega_x/, "-mega-x")
    .replace(/__mega_y/, "-mega-y")
    .replace(/__mega-x/, "-mega-x")
    .replace(/__mega-y/, "-mega-y")
    .replace(/__mega__x/, "-mega-x")
    .replace(/__mega__y/, "-mega-y")
    .replace(/__mega/, "-mega")
    .replace(/__alola/, "-alola")
    .replace(/__hisui/, "-hisui")
    .replace(/__galar/, "-galar")
    .replace(/__paldea/, "-paldea");

  return POKEMON_SPRITE_SCALE_OVERRIDES[parsedName] ?? 1.0;
}
